import { createApp } from '@app/adapters/create-app'

import TextPropertyFilesUploader from './../../sections/text-property-files-uploader'
import DocumentsUploader from './../../sections/documents-uploader'
import SchedulerAdapter from '@app/adapters/schedule'
import { formatBytes } from '@app/utils/file-helpers'
import SFetch from '@app/utils/sfetch'

const HIGHLIGHT_CLASS = 'sf-bg-primary-subtle'

export default class DocumentSelect {
  constructor({ firmId, companyId, settings }) {
    this.settings = settings
    this.uploadsInProgress = 0
    this.type = this.settings.type
    this.locked = this.settings.locked
    this.changesToSave = false

    if (this.type === 'file_collection') {
      this.fileCollectionValue = this.settings.fileCollectionValue
      this.fileCollectionValue.forEach((file) => {
        file.linkType = 'Document'
        file.unique_id = `document-${file.document_id}`
      })
    }

    this.deletedDocuments = []

    $.get(DocumentSelect.modalPath(firmId, companyId), {}, (data) => {
      const $html = $(data.html)
      this.$html = $html
      $html.sfModal({
        show: true,
        canClose: () => {
          if (this.uploadsInProgress > 0) return false
          if (this.changesToSave) {
            $html.data('sf.modal')?.unFocusModal()

            const SfScheduler = SchedulerAdapter()
            SfScheduler.setTimeout(() => {
              window.sfConfirm({
                question: I18n.t(
                  'js.document_selection.are_you_sure_you_want_to_cancel_without_saving'
                ),
                onDone: () => $html.data('sf.modal')?.focusModal(),
                onConfirm: () => {
                  this.changesToSave = false
                  this.closeModal()
                },
              })
            })
            return false
          } else {
            return true
          }
        },
        onHide: () => $html.remove(),
      })

      this.initializeGSUpload($html)

      if (this.type === 'file_collection') {
        $html
          .find(
            '[data-behaviour~="document-select-visible-only-for-file-collection"]'
          )
          .show()
        $html.find('input[type="file"]').attr('multiple', 'multiple')

        if (this.locked) {
          $html
            .find('[data-behaviour~="document-select-hide-when-locked"]')
            .remove()
          $html
            .find('[data-behaviour~="document-select-show-when-locked"]')
            .show()
        }
      } else {
        $html
          .find(
            '[data-behaviour~="document-select-visible-only-for-file-collection"]'
          )
          .remove()
        $html.find('input[type="file"]').removeAttr('multiple')
      }

      this.refreshFileCollectionVisibility($html)
      this.renderFileCollection($html)
      this.highlightSelectedPermanentDocuments($html)

      if (this.settings.fileCollectionValue.length > 0)
        $html
          .find('[data-behaviour~="document-select-file-collection-download"]')
          .show()

      // Opening file upload dialog
      $html.on(
        'click',
        '[data-behaviour~="select-document-browse-link"]',
        (e) => {
          e.preventDefault()
          $html.find('input[type=file]').get(0).click()
        }
      )

      // Selecting or adding permanent entries to the collection
      $html.on('click', '[data-behaviour~="document-select-link"]', (e) => {
        const $target = $(e.currentTarget)

        const data = {
          linkType: 'PermanentDocument',
          permanent_document_id: $target.data('permanent-document-id'),
        }

        if (this.type === 'file_collection') {
          this.changesToSave = true
          const documentId = $target.data('document-id')

          const existing = this.fileCollectionValue.filter(
            (e) =>
              (documentId && e.document_id === documentId) ||
              (data.permanent_document_id &&
                e.permanent_document_id === data.permanent_document_id)
          )

          // Toggle file selection
          if (existing.length === 0) {
            this.fileCollectionValue.push({
              ...data,
              name: $target.text(),
              document_id: documentId,
              unique_id: `permanent-document-${data.permanent_document_id}`,
            })
            this.deletedDocuments = this.deletedDocuments.filter(
              (d) => d !== documentId
            )
          } else {
            if (documentId) this.deletedDocuments.push(documentId)
            this.fileCollectionValue.splice(
              this.fileCollectionValue.indexOf(existing[0]),
              1
            )
          }

          this.renderFileCollection($html)
          this.highlightSelectedPermanentDocuments($html)
        } else {
          this.settings.success(data)
          this.closeModal()
        }

        return false
      })

      // Navigating to a permanent category
      $html.on('click', '[data-behaviour~="category-select-link"]', (e) => {
        const url = $(e.currentTarget).data('category-url')
        $.get(url, {}, (data) => {
          const selector =
            this.type === 'file_collection'
              ? '[data-behaviour~="select-document-browser"]'
              : '.sf-modal-inner'

          $html.find(selector).replaceWith($(data.html).find(selector))
          this.highlightSelectedPermanentDocuments($html)
        })

        return false
      })

      // Removing files selected for attachment
      $html.on(
        'click',
        '[data-behaviour~="document-select-file-collection-delete"]',
        (e) => {
          e.preventDefault()
          const $item = $(e.currentTarget).parents('[data-unique-id]')

          this.changesToSave = true

          // only document-id needs to be submit to the server as those have already been saved
          const documentId = $item.data('document-id')
          if (documentId) this.deletedDocuments.push(documentId)

          const uniqueId = $item.data('unique-id')
          this.fileCollectionValue = this.fileCollectionValue.filter(
            (e) => e.unique_id !== uniqueId
          )

          this.renderFileCollection($html)
          this.highlightSelectedPermanentDocuments($html)

          return false
        }
      )

      // Saving the list of file attachments
      $html.on(
        'click',
        '[data-behaviour~="document-select-file-collection-save"]',
        (e) => {
          this.hideErrors($html)
          if (e.target.hasAttribute('data-file-collection-save-in-progress')) {
            e.preventDefault()
            return
          }

          e.target.setAttribute('data-file-collection-save-in-progress', '')
          this.settings.success(
            this.fileCollectionValue,
            this.deletedDocuments,
            () => {
              window.eventHub.$emit('refreshCommunicationData')
              this.closeModal()
            },
            (xhr) => {
              if (xhr?.responseJSON?.message) {
                this.showError($html, xhr.responseJSON.message)
              }
            }
          )
        }
      )

      $html.on(
        'click',
        '[data-behaviour~="document-select-file-collection-download"]',
        () => {
          this.settings.download_documents()
        }
      )

      $(document).trigger('documentsUploadRendered')
    }).always(() => {
      if (
        this.settings.modalOpened &&
        typeof this.settings.modalOpened === 'function'
      ) {
        this.settings.modalOpened()
      }
      // To make sure only one modal is open
      window.modalCitizen.documentSelectModalOpened()
    })
  }

  highlightSelectedPermanentDocuments($html) {
    if (this.type !== 'file_collection') return

    const $fileBrowser = $html.find(
      '[data-behaviour~="select-document-browser"]'
    )
    $fileBrowser.find('[data-document-id]').removeClass(HIGHLIGHT_CLASS)

    this.fileCollectionValue.forEach((file) => {
      if (file.document_id)
        $fileBrowser
          .find(`[data-document-id=${file.document_id}]`)
          .addClass(HIGHLIGHT_CLASS)
    })
  }

  renderFileCollection($html) {
    if (this.type !== 'file_collection') return

    const $collection = $html.find(
      '[data-behaviour~="document-select-file-collection"]'
    )
    $collection.html('')

    this.fileCollectionValue.forEach((file) => {
      const $item = $('<div class="sf-tab-item sf-pointer">')
      let behaviour = 'document-select-file-collection-item'

      if (file.document_id) {
        $item.attr('data-document-id', file.document_id)
        behaviour = `${behaviour} document-preview-link`
      }

      $item.attr('data-behaviour', behaviour)

      if (file.permanent_document_id)
        $item.attr('data-permanent-document-id', file.permanent_document_id)

      $item.attr('data-unique-id', file.unique_id)

      $collection.append($item)

      const $level = $('<div class="sf-level sf-level-between">')
      $item.append($level)

      const $buttons = $(
        '<div class="sf-level-item document-select__item-container-buttons">'
      )

      if (!this.locked) {
        if (this.supportPeriodSpecificDocs) {
          const $downloadButton = $('<a class="sf-a">')
            .attr('data-behaviour', 'document-download-link')
            .attr('data-document-id', file.document_id)
          $downloadButton.append('<i class="icon icon-download"></i>')
          $buttons.append($downloadButton)

          const $previewButton = $('<a class="sf-a">')
            .attr('data-behaviour', 'document-preview-link')
            .attr('data-document-id', file.document_id)
          $previewButton.append('<i class="icon icon-eye"></i>')
          $buttons.append($previewButton)
        }

        const $deleteButton = $('<a class="sf-a">').attr(
          'data-behaviour',
          'document-select-file-collection-delete'
        )
        $deleteButton.append($('<i class="icon-trash">'))
        $buttons.append($deleteButton)
      }

      let name = file.name

      // Add tooltip to long document names
      $item.attr('data-toggle', 'tooltip')
      $item.attr('data-placement', 'top')
      $item.attr('title', file.name)

      // Chop extension from uploads as we also don't display it for other item types
      if (file.linkType === 'DirectUpload') name = name.replace(/\.[^.$]+$/, '')

      $level.append(
        $(
          '<div class="sf-level-item sf-truncate sf-noscroll document-select__item-container-name">'
        ).text(name)
      )

      if (file.size)
        $level.append(
          $(
            '<div class="sf-level-item sf-truncate sf-noscroll document-select__item-container-size">'
          ).html(formatBytes(file.size))
        )

      $level.append($buttons)

      if (file.duplicate_document) {
        const $link = $(
          `<a href="#">${I18n.t(
            'js.live_inputs.documents.use_existing_file'
          )}</a>`
        )
        $link.on('click', (e) => {
          e.preventDefault()

          file.duplicate_document.attach()
        })
        const $duplicate = $(
          `<div class="sf-c-danger">${I18n.t(
            'js.live_inputs.documents.duplicate_error',
            { attachment: file.duplicate_document.attachments[0] }
          )}, </div>`
        )
        $duplicate.append($link)
        $duplicate.append(` ${I18n.t('js.live_inputs.documents.instead')}`)
        $item.append($duplicate)
      }
    })

    this.refreshFileCollectionVisibility($html)
  }

  initializeGSUpload($html) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this

    $html.find("[data-behaviour='documents-select-upload']").each(function () {
      const $progressContainer = $(
        '[data-behaviour="document-select-in-progress-uploads"]'
      )

      const dataAttrs = $(this).data()
      self.supportPeriodSpecificDocs = dataAttrs.supportPeriodSpecific

      const onFileAttached = (documents) => {
        documents.forEach((doc) => {
          let data = {}

          if (doc.document) {
            data = {
              linkType: 'Document',
              name: doc.title,
              document_id: doc.id,
              unique_id: 'existing-document-#{doc.id}',
            }
          } else {
            data = {
              linkType: 'PermanentDocument',
              permanent_document_id: doc.id,
              name: doc.title,
              unique_id: 'permanent-document-#{doc.id}',
              document_id: doc.documentId,
            }
          }

          if (self.type === 'file_collection') {
            const existing = self.fileCollectionValue.find(
              (e) => e.document_id === data.document_id
            )
            if (!existing) {
              self.fileCollectionValue.push(data)
              self.changesToSave = true
            }
          } else if (self.type === 'replacement') {
            self.settings.replace(data)
            self.closeModal($html)
          } else {
            self.settings.success(data)
            self.closeModal($html)
          }

          self.renderFileCollection($html)
          self.highlightSelectedPermanentDocuments($html)
        })
      }

      const checkForDuplicateDocument = (document, addFileToCollection) => {
        SFetch.json(
          window.Silverfin.Routes.check_for_duplicate_document_path({
            firm_id: window.Silverfin.currentFirmId,
            company_id: window.Silverfin.currentCompanyId,
            q: document.name,
            s: document.size,
          })
        ).then((duplicate) => {
          if (duplicate) {
            duplicate.attach = () => {
              self.fileCollectionValue = self.fileCollectionValue.filter(
                (d) => d !== document
              )
              onFileAttached([
                {
                  id: duplicate.id,
                  title: duplicate.title,
                  document: duplicate,
                },
              ])
            }
            document.duplicate_document = duplicate
          }
          addFileToCollection(document)
        })
      }

      const handleFileUploaded = ({ blob }, uuid) => {
        $progressContainer.find(`[data-id='${uuid}']`).remove()
        const data = {
          linkType: 'DirectUpload',
          link: blob.signed_id,
          name: blob.filename,
          contentType: blob.content_type,
          size: blob.byte_size,
          unique_id: `upload-${uuid}`,
        }

        if (self.type === 'file_collection') {
          const addFileToCollection = (file) => {
            self.refreshFileCollectionVisibility($html)
            self.uploadsInProgress -= 1
            self.fileCollectionValue.push(file)
            self.renderFileCollection($html)
          }

          if (self.supportPeriodSpecificDocs) {
            checkForDuplicateDocument(data, addFileToCollection)
          } else {
            addFileToCollection(data)
          }
        } else if (self.type === 'replacement') {
          self.settings.replace(data)
          self.closeModal($html)
        } else {
          self.settings.success(data)
          self.closeModal($html)
        }
      }

      const onFileUploadProgress = (uuid, file, progress) => {
        const $fileTemplate = $progressContainer.find(
          "[data-id='" + uuid + "']"
        )
        if ($fileTemplate.length > 0) {
          $fileTemplate
            .find('.sf-progress .sf-progress-complete')
            .css('width', progress + '%')
        } else {
          $progressContainer.append(
            "<div class='sf-tab-item sf-pointer' data-id=" +
              uuid +
              "><div class='sf-level sf-level-between'><div class='sf-level-item sf-truncate sf-noscroll'>" +
              file.file.name +
              "</div><div class='sf-level-item'><div class='sf-progress sf-progress-animate' style='width: 64px'><div class='bar sf-progress-complete sf-bg-primary' style='width: " +
              progress +
              "%'></div></div></div></div></div>"
          )
          self.refreshFileCollectionVisibility($html)
        }
      }

      if (self.supportPeriodSpecificDocs) {
        createApp(
          DocumentsUploader,
          {
            accept: dataAttrs.accept,
            replacement: typeof self.settings.replace === 'function',
            onFileUploaded: handleFileUploaded,
            onFileUploadProgress,
            onFileAttached,
          },
          {
            singletonId: 'live-inputs-documents-uploader',
          }
        ).mount(this)
      } else {
        createApp(
          TextPropertyFilesUploader,
          {
            accept: dataAttrs.accept,
            onFileUploaded: handleFileUploaded,
            onFileUploadProgress: onFileUploadProgress,
          },
          {
            singletonId: 'live-inputs-text-property-files-uploader',
          }
        ).mount(this)
      }
    })
  }

  refreshFileCollectionVisibility($html) {
    if (this.type !== 'file_collection' && this.type !== 'replacement') return

    const haveFiles =
      $html.find('[data-behaviour~="document-select-file-collection"] > *')
        .length > 0
    const haveUploads =
      $html.find('[data-behaviour~="document-select-in-progress-uploads"] > *')
        .length > 0

    $html
      .find(
        '[data-behaviour~="document-select-visible-only-for-file-collection"]'
      )
      .show()

    $html
      .find('[data-behaviour~="document-select-file-collection-container"]')
      .toggle(haveFiles || haveUploads)
    $html
      .find('[data-behaviour~="document-select-file-collection"]')
      .toggle(haveFiles)
    $html
      .find('[data-behaviour~="document-select-in-progress-uploads"]')
      .toggle(haveUploads)

    // currently always visible so you can actually save an empty list of files
    // $html.find('[data-behaviour~="document-select-file-collection-save-container"]').toggle((haveFiles || haveUploads)

    $html
      .find('[data-behaviour~="document-select-file-collection-save"]')
      .toggle(!haveUploads)
    $html
      .find('[data-behaviour~="document-select-file-collection-save-disabled"]')
      .toggle(haveUploads)
  }

  closeModal() {
    this.$html.remove()
  }

  showError($html, message) {
    $html.find('.sf-alert-danger').html(message).removeClass('sf-hidden')
  }

  hideErrors($html) {
    $html.find('.sf-alert-danger').html('').addClass('sf-hidden')
  }

  static modalPath(firmId, companyId) {
    return Silverfin.Routes.document_select_permanent_entries_path(
      firmId,
      companyId
    )
  }
}
